import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Card,
  type ChartConfig,
  ChartContainer,
  CurrencyAmountUI,
  cn,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Txt,
} from "@repo/ui";
import { isCurrency, neverFunc } from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import {
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PiArrowsOut,
  PiBuildingOffice,
  PiCalendar,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiCopy,
  PiEye,
  PiEyeClosed,
  PiInfo,
  PiMoney,
  PiSlidersHorizontal,
  PiX,
} from "react-icons/pi";
import {
  Bar,
  BarChart,
  Cell,
  Tooltip as RechartsTooltip,
  type TooltipProps,
  YAxis,
} from "recharts";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AssetCardFieldsFragment,
  CashflowNotableTxnFragment,
  MatchLedgerTransactionFragment,
  MatchProposalFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const PAGE_SIZE = 20;

const orgsDocument = graphql(`
  query AdminExternalTxMatchProposalOrgs {
    organisations {
      id
      name
      squareLogoUrl
    }
  }
`);

const proposalsDocument = graphql(`
  query AdminExternalTransactionMatchProposals(
    $first: Int!
    $after: String
    $organisationIds: [ID!]
  ) {
    externalTransactionMatchProposals(
      first: $first
      after: $after
      filter: { organisationIds: $organisationIds }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      externalTransactionMatchProposals {
        ...MatchProposal
      }
    }
  }

  fragment MatchLedgerTransaction on LedgerTransaction {
    id
    shortRef
    effectiveAt
    description
    ledgerEntries {
      direction
      unitAmount {
        amount
        unit
      }
      ledgerAccount {
        id
        ... on AssetLedgerAccount {
          assetLedgerAccountKind
          asset {
            ...AssetCardFields
          }
        }
      }
    }
  }

  fragment MatchProposal on ExternalTransactionMatchProposal {
    id
    shortRef
    ledgerTransaction {
      ...MatchLedgerTransaction
    }
    matchedLedgerTransaction {
      ...MatchLedgerTransaction
    }
  }

`);

const cashflowReportDocument = graphql(`
  query AdminCashflowReport($organisationId: ID!, $periodBeginning: CalendarDate!, $periodEnding: CalendarDate!) {
    organisation(id: $organisationId) {
      id
      name
      cashflowReport(periodBeginning: $periodBeginning, periodEnding: $periodEnding) {
        periodBeginning
        periodEnding
        businessEntityBreakdown {
          businessEntity {
            id
            name
          }
          values {
            ...CashflowLineItemValues
          }
          assetBreakdown {
            asset {
              id
              name
              ...AssetCardFields
            }
            values {
              ...CashflowLineItemValues
            }
            midPeriodOpeningDate
            midPeriodTxnHistoryBeginning
          }
        }
      }
    }
  }

  fragment CashflowNotableTxn on CashflowNotableTransaction {
    id
    effectiveAt
    description
    amount {
      amount
      currency
    }
    direction
    asset {
      name
    }
    otherBusinessEntity {
      id
      name
    }
    transfer {
      id
      shortRef
    }
    proposal {
      id
      ledgerTransaction {
        id
        shortRef
        effectiveAt
        description
        ledgerEntries {
          direction
          unitAmount {
            amount
            unit
          }
          ledgerAccount {
            id
            ... on AssetLedgerAccount {
              assetLedgerAccountKind
              asset {
                id
                name
                businessEntity {
                  id
                  name
                }
              }
            }
          }
        }
      }
      matchedLedgerTransaction {
        id
        shortRef
        effectiveAt
        description
        ledgerEntries {
          direction
          unitAmount {
            amount
            unit
          }
          ledgerAccount {
            id
            ... on AssetLedgerAccount {
              assetLedgerAccountKind
              asset {
                id
                name
                businessEntity {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }

  fragment CashflowLineItemValues on CashflowReportLineItemValues {
    opening {
      amount
      currency
    }
    closing {
      amount
      currency
    }
    net {
      amount
      currency
    }
    unattributed {
      amount
      currency
    }
    assumedExternalCashflowNet {
      amount
      currency
    }
    unrealisedGains {
      amount
      currency
    }
    totalIn {
      amount
      currency
    }
    totalOut {
      amount
      currency
    }
    externalIn {
      amount
      currency
    }
    externalOut {
      amount
      currency
    }
    interOrgIn {
      amount
      currency
    }
    interOrgOut {
      amount
      currency
    }
    intraEntityIn {
      amount
      currency
    }
    intraEntityOut {
      amount
      currency
    }
    externalInNotable {
      ...CashflowNotableTxn
    }
    externalInOtherCount
    externalOutNotable {
      ...CashflowNotableTxn
    }
    externalOutOtherCount
    interOrgInNotable {
      ...CashflowNotableTxn
    }
    interOrgInOtherCount
    interOrgOutNotable {
      ...CashflowNotableTxn
    }
    interOrgOutOtherCount
    intraEntityNotable {
      ...CashflowNotableTxn
    }
    intraEntityOtherCount
    interOrgInByEntity {
      businessEntity { id name }
      amount { amount currency }
    }
    interOrgOutByEntity {
      businessEntity { id name }
      amount { amount currency }
    }
    interOrgInByAsset {
      asset { id name businessEntity { id name } }
      amount { amount currency }
    }
    interOrgOutByAsset {
      asset { id name businessEntity { id name } }
      amount { amount currency }
    }
  }

`);

export const Route = createFileRoute("/__auth/cashflow-reports")({
  head: () => opsHead("Cashflow Reports"),
  validateSearch: z.object({
    organisationId: z
      .string()
      .optional()
      .catch(undefined)
      .default("org_3AMh9ghkteDrGmLQrqTCB6Rs9nH"),
    cashflowMonth: z.string().optional().catch(undefined),
    reportMode: z
      .enum(["month", "year-cal", "year-fin"])
      .optional()
      .catch(undefined)
      .default("month"),
    fullscreen: z.boolean().optional().catch(undefined),
    hideZeroRows: z.boolean().optional().catch(undefined),
  }),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function LedgerTransactionCell({ lt }: { lt: MatchLedgerTransactionFragment }) {
  const primaryEntry =
    lt.ledgerEntries?.find((e) => {
      if (
        e.ledgerAccount &&
        e.ledgerAccount?.__typename === "AssetLedgerAccount"
      ) {
        return e.ledgerAccount.assetLedgerAccountKind === "BALANCE";
      }
      return false;
    }) ?? lt.ledgerEntries?.[0];
  const asset =
    primaryEntry?.ledgerAccount?.__typename === "AssetLedgerAccount"
      ? primaryEntry?.ledgerAccount?.asset
      : null;
  const amount = primaryEntry?.unitAmount;

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/transactions/$transactionId"
          params={{ transactionId: lt.id }}
          className="font-mono text-info text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          #{lt.shortRef}
        </Link>
        <DateUI date={lt.effectiveAt} variant="DATE" />
      </div>
      {asset && <AccountCard account={asset} variant="LABEL" />}
      {lt.description && (
        <p className="text-secondary text-xs">{lt.description}</p>
      )}
      {amount && isCurrency(amount.unit) && (
        <div className="flex items-center justify-end gap-1 text-sm">
          <span
            className={
              primaryEntry?.direction === "CREDIT"
                ? "text-success"
                : "text-danger"
            }
          >
            {primaryEntry?.direction === "CREDIT" ? "CR" : "DB"}
          </span>
          <CurrencyAmountUI amount={amount.amount} currency={amount.unit} />
        </div>
      )}
    </Card>
  );
}

const columns: ColumnDef<MatchProposalFragment>[] = [
  {
    id: "ledgerTransaction",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Txn ID" />
    ),
    cell: ({ row }) => (
      <LedgerTransactionCell lt={row.original.ledgerTransaction} />
    ),
  },
  {
    id: "delta",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Delta" />
    ),
    cell: ({ row }) => {
      const { ledgerTransaction: lt, matchedLedgerTransaction: mlt } =
        row.original;
      const days = Math.abs(differenceInDays(lt.effectiveAt, mlt.effectiveAt));

      const getDebitAmount = (
        entries?: MatchLedgerTransactionFragment["ledgerEntries"] | null,
      ) => {
        if (!entries?.length) {
          return null;
        }
        const entry =
          entries.find((e) => e.direction === "DEBIT") ?? entries[0];
        return entry.unitAmount;
      };
      const a1 = getDebitAmount(row.original.ledgerTransaction.ledgerEntries);
      const a2 = getDebitAmount(
        row.original.matchedLedgerTransaction.ledgerEntries,
      );
      if (!a1 || !a2 || a1.unit !== a2.unit) {
        return <div className="text-right text-secondary text-sm">—</div>;
      }
      const delta = a1.amount.minus(a2.amount).abs();

      return (
        <div>
          <div className="text-center text-sm">
            {days < 1 ? "<1" : days} {days === 1 ? "day" : "days"}
          </div>
          <div className="text-center text-sm">
            {isCurrency(a1.unit) ? (
              <CurrencyAmountUI amount={delta} currency={a1.unit} />
            ) : (
              `${delta.toFixed(2)} ${a1.unit}`
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: "matchedLedgerTransaction",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Match Txn ID" />
    ),
    cell: ({ row }) => (
      <LedgerTransactionCell lt={row.original.matchedLedgerTransaction} />
    ),
  },
];

type CashflowRow = {
  id: string;
  name: string;
  opening: Decimal;
  externalIn: Decimal;
  externalOut: Decimal;
  interOrgIn: Decimal;
  interOrgOut: Decimal;
  intraEntityIn: Decimal;
  intraEntityOut: Decimal;
  closing: Decimal;
  net: Decimal;
  unattributed: Decimal;
  assumedExternalCashflowNet: Decimal;
  unrealisedGains: Decimal;
  currency: string;
  isGroup?: boolean;
  isAsset?: boolean;
  parentId?: string;
  assetNativeCurrency?: string;
  midPeriodOpeningDate?: Date;
  midPeriodTxnHistoryBeginning?: Date;
  assetObj?: AssetCardFieldsFragment;
  externalInNotable: CashflowNotableTxnFragment[];
  externalInOtherCount: number;
  externalOutNotable: CashflowNotableTxnFragment[];
  externalOutOtherCount: number;
  interOrgInNotable: CashflowNotableTxnFragment[];
  interOrgInOtherCount: number;
  interOrgOutNotable: CashflowNotableTxnFragment[];
  interOrgOutOtherCount: number;
  intraEntityNotable: CashflowNotableTxnFragment[];
  intraEntityOtherCount: number;
  interOrgInByEntity: {
    entityId: string;
    entityName: string;
    amount: Decimal;
  }[];
  interOrgOutByEntity: {
    entityId: string;
    entityName: string;
    amount: Decimal;
  }[];
  interOrgInByAsset: {
    assetId: string;
    assetName: string;
    entityName: string;
    amount: Decimal;
  }[];
  interOrgOutByAsset: {
    assetId: string;
    assetName: string;
    entityName: string;
    amount: Decimal;
  }[];
};

function fmtAmount(amount: Decimal, isOut = false): string {
  const abs = amount.abs().toDecimalPlaces(2);
  const formatted = abs.toNumber().toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return isOut || amount.isNegative() ? `(${formatted})` : formatted;
}

function NumCell({
  amount,
  isOut = false,
  bold = false,
}: {
  amount: Decimal;
  isOut?: boolean;
  bold?: boolean;
}) {
  return (
    <div className={`text-right tabular-nums${bold ? "font-semibold" : ""}`}>
      {fmtAmount(amount, isOut)}
    </div>
  );
}

function NumCellWithHover({
  amount,
  isOut = false,
  bold = false,
  notable,
  otherCount,
  entityName,
  assumedExternalCashflowNet,
  assumedExternalCashflowNetCurrency,
  onHighlight,
  onExtraHighlight,
}: {
  amount: Decimal;
  isOut?: boolean;
  bold?: boolean;
  notable: CashflowNotableTxnFragment[];
  otherCount: number;
  entityName: string;
  assumedExternalCashflowNet?: Decimal;
  assumedExternalCashflowNetCurrency?: string;
  onHighlight?: (entityIds: string[]) => void;
  onExtraHighlight?: (entityId: string | null) => void;
}) {
  const highlightIds = [
    ...new Set(
      notable.flatMap((t) =>
        t.otherBusinessEntity ? [t.otherBusinessEntity.id] : [],
      ),
    ),
  ];

  const [isOpen, setIsOpen] = useState(false);

  // assumedExternalCashflowNet is a single net figure — only relevant to
  // whichever side (in/out) its sign corresponds to. Decimal treats zero as
  // positive, so isZero() must be checked explicitly to avoid showing it on
  // the "in" side when there's nothing to report.
  const relevantAssumedAmount =
    assumedExternalCashflowNet &&
    !assumedExternalCashflowNet.isZero() &&
    (isOut
      ? assumedExternalCashflowNet.isNegative()
      : assumedExternalCashflowNet.isPositive())
      ? assumedExternalCashflowNet.abs()
      : undefined;

  if (notable.length === 0 && otherCount === 0 && !relevantAssumedAmount) {
    return <NumCell amount={amount} isOut={isOut} bold={bold} />;
  }

  const clearHighlights = () => {
    onHighlight?.([]);
    onExtraHighlight?.(null);
  };

  return (
    <HoverCard
      openDelay={100}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          clearHighlights();
        }
      }}
    >
      <HoverCardTrigger asChild>
        <div
          className={`relative cursor-default text-right tabular-nums underline decoration-dotted${bold ? "font-semibold" : ""}`}
          onMouseEnter={() => onHighlight?.(highlightIds)}
          onMouseLeave={() => {
            if (!isOpen) {
              clearHighlights();
            }
          }}
        >
          {fmtAmount(amount, isOut)}
          {relevantAssumedAmount && (
            <span className="absolute top-0 -right-2.5 text-tertiary text-xs">
              *
            </span>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        padding="none"
        className="max-h-[22rem] w-[38rem] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="px-7 pt-5 pb-2">
          <Txt variant="field-label">
            Transactions {isOut ? "from" : "to"} {entityName}
          </Txt>
        </div>
        {notable.length > 0 && (
          <>
            {(() => {
              const sorted = [...notable].sort((a, b) => {
                const dateA = new Date(a.effectiveAt).getTime();
                const dateB = new Date(b.effectiveAt).getTime();
                if (dateA !== dateB) {
                  return dateA - dateB;
                }
                return new Decimal(b.amount.amount).comparedTo(
                  new Decimal(a.amount.amount),
                );
              });

              // Deduplicate proposal pairs: when txn A shows its counterpart B,
              // mark B as covered so the mirrored row (B showing A) is skipped.
              const seenAsCounterpart = new Set<string>();
              const deduped = sorted.filter((t) => {
                if (seenAsCounterpart.has(t.id)) {
                  return false;
                }
                if (t.proposal) {
                  const counterpartId =
                    t.proposal.ledgerTransaction.id === t.id
                      ? t.proposal.matchedLedgerTransaction.id
                      : t.proposal.ledgerTransaction.id;
                  seenAsCounterpart.add(counterpartId);
                }
                return true;
              });

              return (
                <div className="flex flex-col gap-2 px-4 pt-1 pb-3">
                  {deduped.map((t) => {
                    const dt = DateTime.fromJSDate(new Date(t.effectiveAt));
                    const dateLabel = dt.toFormat("d MMM yyyy");
                    let otherSide = null;
                    if (t.proposal) {
                      const isFirst = t.proposal.ledgerTransaction.id === t.id;
                      otherSide = isFirst
                        ? t.proposal.matchedLedgerTransaction
                        : t.proposal.ledgerTransaction;
                    }
                    const otherEntry = otherSide?.ledgerEntries?.find(
                      (e) =>
                        e.ledgerAccount?.__typename === "AssetLedgerAccount" &&
                        (e.ledgerAccount as { assetLedgerAccountKind?: string })
                          .assetLedgerAccountKind === "BALANCE",
                    );
                    const counterpart =
                      otherSide &&
                      otherEntry?.unitAmount &&
                      isCurrency(otherEntry.unitAmount.unit)
                        ? {
                            side: otherSide,
                            amount: otherEntry.unitAmount.amount,
                            unit: otherEntry.unitAmount.unit,
                          }
                        : null;

                    return (
                      <div
                        key={t.id}
                        className="rounded-md px-3 py-2 text-xs transition-colors hover:bg-neutral-muted"
                        onMouseEnter={() =>
                          t.otherBusinessEntity &&
                          onExtraHighlight?.(t.otherBusinessEntity.id)
                        }
                        onMouseLeave={() => onExtraHighlight?.(null)}
                      >
                        {/* Top row: DATE · ACCOUNT (linked) · AMOUNT */}
                        <div className="flex items-baseline gap-0">
                          <Link
                            to="/transactions/$transactionId"
                            params={{ transactionId: t.id }}
                            className="font-medium text-primary underline decoration-1 underline-offset-2 hover:text-info"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t.asset?.name
                              ? `${dateLabel} · ${t.asset?.name}`
                              : dateLabel}
                          </Link>
                          <span className="ml-auto font-medium text-primary tabular-nums">
                            <CurrencyAmountUI
                              amount={t.amount.amount}
                              currency={t.amount.currency}
                            />
                          </span>
                        </div>
                        {counterpart &&
                          (() => {
                            const balanceEntry =
                              counterpart.side.ledgerEntries?.find(
                                (e) =>
                                  e.ledgerAccount?.__typename ===
                                    "AssetLedgerAccount" &&
                                  (
                                    e.ledgerAccount as {
                                      assetLedgerAccountKind?: string;
                                    }
                                  ).assetLedgerAccountKind === "BALANCE",
                              );
                            const counterpartAssetName = (
                              balanceEntry?.ledgerAccount as
                                | { asset?: { name?: string } }
                                | undefined
                            )?.asset?.name;
                            const counterpartEntityName =
                              t.otherBusinessEntity?.name ??
                              (
                                balanceEntry?.ledgerAccount as
                                  | {
                                      asset?: {
                                        businessEntity?: { name?: string };
                                      };
                                    }
                                  | undefined
                              )?.asset?.businessEntity?.name;
                            const arrow = t.direction === "DEBIT" ? "→" : "←";
                            return (
                              <div className="mt-0.5 text-tertiary text-xs">
                                {arrow}
                                {t.otherBusinessEntity &&
                                counterpartEntityName ? (
                                  <>
                                    {" "}
                                    {counterpartEntityName}
                                    {counterpartAssetName && (
                                      <>
                                        <span className="mx-1">·</span>
                                        {counterpartAssetName}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  counterpartAssetName && (
                                    <> {counterpartAssetName}</>
                                  )
                                )}
                              </div>
                            );
                          })()}
                        {t.description && (
                          <div className="mt-0.5 truncate font-mono text-tertiary text-xs">
                            {t.description}
                          </div>
                        )}
                        {/* Other entity / transfer tags */}
                        {!counterpart &&
                          (t.otherBusinessEntity || t.transfer) && (
                            <div className="mt-1 flex items-center gap-2">
                              {t.otherBusinessEntity && (
                                <span className="truncate font-medium text-info">
                                  {t.otherBusinessEntity.name}
                                </span>
                              )}
                              {t.transfer && (
                                <Link
                                  to="/transfers/$transferId"
                                  params={{ transferId: t.transfer.id }}
                                  className="ml-auto shrink-0 rounded bg-info-muted px-1 py-0.5 font-medium text-[10px] text-info hover:bg-info-muted/70"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Transfer #{t.transfer.shortRef}
                                </Link>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}
        {relevantAssumedAmount && (
          <div className="flex items-center gap-1 px-7 pb-3 text-xs">
            <span className="font-medium text-primary">
              *{" "}
              <CurrencyAmountUI
                amount={relevantAssumedAmount}
                currency={assumedExternalCashflowNetCurrency}
              />{" "}
              of untracked fund movements
            </span>
            <TooltipProvider>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    <PiInfo className="text-tertiary" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 text-left">
                  This can happen when the account&apos;s data provider
                  doesn&apos;t give us its full transaction history, or when the
                  transaction data we do have is incomplete or inconsistent.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        {otherCount > 0 && (
          <div className="px-7 pb-3 text-tertiary text-xs">
            {`+${otherCount} smaller transaction${otherCount === 1 ? "" : "s"}`}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function RightHeader({ title }: { title: string }) {
  return <div className="text-right">{title}</div>;
}

function isStripeAsset(asset: AssetCardFieldsFragment | undefined): boolean {
  return (
    asset?.__typename === "ExternalBankAccountAsset" &&
    asset.details?.__typename === "StripeExternalAccountDetails"
  );
}

function externalAccountDetailsTypenameLabel(
  typename: NonNullable<
    Extract<
      AssetCardFieldsFragment,
      { __typename: "ExternalBankAccountAsset" }
    >["details"]
  >["__typename"],
): string {
  switch (typename) {
    case "AirwallexExternalAccountDetails":
      return "Airwallex";
    case "AkahuExternalAccountDetails":
      return "Akahu";
    case "BrexExternalAccountDetails":
      return "Brex";
    case "JpmcExternalAccountDetails":
      return "JPMorgan Chase";
    case "ManualEntryExternalAccountDetails":
      return "Manual Entry";
    case "MercuryExternalAccountDetails":
      return "Mercury";
    case "PlaidExternalAccountDetails":
      return "Plaid";
    case "QuilttExternalAccountDetails":
      return "Quiltt";
    case "RampExternalAccountDetails":
      return "Ramp";
    case "SaltEdgeExternalAccountDetails":
      return "Salt Edge";
    case "SissExternalAccountDetails":
      return "SISS";
    case "SkriptExternalAccountDetails":
      return "Skript";
    case "StripeExternalAccountDetails":
      return "Stripe";
    default:
      return neverFunc(typename);
  }
}

function dataProviderName(asset: AssetCardFieldsFragment | undefined): string {
  if (asset?.__typename !== "ExternalBankAccountAsset" || !asset.details) {
    return "Primary";
  }
  return externalAccountDetailsTypenameLabel(asset.details.__typename);
}

function unattributedSeverityClasses(total: Decimal): string {
  const abs = total.abs();
  if (abs.lt(1_000)) {
    return "border-success bg-success-muted/20";
  }
  if (abs.lt(30_000)) {
    return "border-warning bg-warning-muted/20";
  }
  if (abs.lte(1_000_000)) {
    return "border-warning bg-warning/20";
  }
  return "border-danger bg-danger-muted/20";
}

type EntityBreakdown = {
  entityId: string;
  entityName: string;
  amount: Decimal;
};
type AssetBreakdown = {
  assetId: string;
  assetName: string;
  entityName: string;
  amount: Decimal;
};

function InterOrgBreakdownCell({
  amount,
  bold,
  isOut,
  entityBreakdown,
  assetBreakdown,
  onExtraHighlight,
}: {
  amount: Decimal;
  bold?: boolean;
  isOut?: boolean;
  entityBreakdown: EntityBreakdown[];
  assetBreakdown: AssetBreakdown[];
  onExtraHighlight?: (entityId: string | null) => void;
}) {
  const [_, setIsOpen] = useState(false);
  const items = assetBreakdown.length > 0 ? assetBreakdown : entityBreakdown;
  if (amount.isZero() || items.length === 0) {
    return <NumCell amount={amount} isOut={isOut} bold={bold} />;
  }

  const isAssetMode = assetBreakdown.length > 0;

  const content = isAssetMode
    ? (() => {
        const groups = new Map<
          string,
          { name: string; entityId: string; items: AssetBreakdown[] }
        >();
        for (const item of assetBreakdown) {
          if (!groups.has(item.entityName)) {
            groups.set(item.entityName, {
              name: item.entityName,
              entityId: item.assetId,
              items: [],
            });
          }
          groups.get(item.entityName)!.items.push(item);
        }
        const sorted = [...groups.values()]
          .map((g) => ({
            ...g,
            total: g.items.reduce((s, i) => s.plus(i.amount), new Decimal(0)),
          }))
          .sort((a, b) => b.total.comparedTo(a.total));
        return sorted.map((g) => (
          <div key={g.name} className="mb-3 last:mb-0">
            <div className="mb-1 font-medium text-secondary text-sm">
              {g.name}
            </div>
            {g.items
              .sort((a, b) => b.amount.comparedTo(a.amount))
              .map((item) => (
                <div
                  key={item.assetId}
                  className="flex items-baseline justify-between gap-4 py-0.5 text-sm"
                >
                  <span className="text-primary">{item.assetName}</span>
                  <span className="font-medium text-primary tabular-nums">
                    {fmtAmount(item.amount, isOut)}
                  </span>
                </div>
              ))}
          </div>
        ));
      })()
    : entityBreakdown
        .sort((a, b) => b.amount.comparedTo(a.amount))
        .map((item) => (
          <div
            key={item.entityId}
            className="-mx-1 flex items-baseline justify-between gap-4 rounded px-1 py-0.5 text-sm transition-colors hover:bg-info-muted"
            onMouseEnter={() => onExtraHighlight?.(item.entityId)}
            onMouseLeave={() => onExtraHighlight?.(null)}
          >
            <span className="text-primary">{item.entityName}</span>
            <span className="font-medium text-primary tabular-nums">
              {fmtAmount(item.amount, isOut)}
            </span>
          </div>
        ));

  return (
    <HoverCard
      openDelay={100}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          onExtraHighlight?.(null);
        }
      }}
    >
      <HoverCardTrigger asChild>
        <div className="cursor-default text-right tabular-nums underline decoration-dotted">
          {fmtAmount(amount, isOut)}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        padding="none"
        className="max-h-72 w-80 overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {content}
      </HoverCardContent>
    </HoverCard>
  );
}

const CHART_CONFIG: ChartConfig = {};

function WaterfallTooltip(props: TooltipProps<number, string>) {
  const { active } = props;
  const payload = (
    props as unknown as {
      payload?: {
        dataKey?: string;
        value?: number;
        payload?: { negative?: boolean };
      }[];
    }
  ).payload;
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }
  const valueEntry = payload.find((p) => p.dataKey === "value");
  if (!valueEntry || !valueEntry.value) {
    return null;
  }
  const isNegative = valueEntry.payload?.negative ?? false;
  return (
    <div className="rounded-md border border-separator bg-white px-2.5 py-1.5 text-xs tabular-nums">
      {fmtAmount(new Decimal(valueEntry.value), isNegative)}
    </div>
  );
}

function CashflowWaterfallChart({
  entityRows,
  groupRow,
  showIntraEntity,
}: {
  entityRows: CashflowRow[];
  groupRow: CashflowRow;
  showIntraEntity: boolean;
}) {
  const opening = groupRow.opening.toNumber();
  const extIn = groupRow.externalIn.toNumber();
  const extOut = groupRow.externalOut.toNumber();
  // Group intercompany nets to zero — sum entity rows for gross flow instead
  const interIn = entityRows.reduce((s, r) => s + r.interOrgIn.toNumber(), 0);
  const interOut = entityRows.reduce((s, r) => s + r.interOrgOut.toNumber(), 0);
  const closing = groupRow.closing.toNumber();

  if (entityRows.length === 0) {
    return null;
  }

  // Waterfall running level
  let r = opening;
  const extInFrom = r;
  r += extIn;
  r -= extOut;
  const extOutFrom = r;
  const intInFrom = r;
  r += interIn;
  r -= interOut;
  const intOutFrom = r;

  // Unattributed and unrealised gains both start from the bottom of cash
  // outflows (extOutFrom level) — sourced from the backend-computed values
  // (summed across entities) rather than re-derived from raw in/out totals.
  const unattributedRaw = groupRow.unattributed.toNumber();
  const unattributed = Math.abs(unattributedRaw);
  const unrealisedGainsRaw = groupRow.unrealisedGains.toNumber();
  const unrealisedGains = Math.abs(unrealisedGainsRaw);

  // Brand colors from tailwind config HSL vars
  const POLAR_DARK = "hsl(213 42% 26%)"; // polar-600   — opening / closing
  const POLAR_LIGHT = "hsl(217 18% 53%)"; // polar-400   — inflows / positive
  const SAND = "hsl(38 31% 90%)"; // brand-sand  — outflows / negative

  const netFrom = Math.min(opening, closing);
  const netTo = Math.max(opening, closing);

  const chartData = [
    { key: "opening", spacer: 0, value: opening, color: POLAR_DARK },
    { key: "extIn", spacer: extInFrom, value: extIn, color: POLAR_LIGHT },
    {
      key: "extOut",
      spacer: extOutFrom,
      value: extOut,
      color: SAND,
      negative: true,
    },
    { key: "intIn", spacer: intInFrom, value: interIn, color: POLAR_LIGHT },
    {
      key: "intOut",
      spacer: intOutFrom,
      value: interOut,
      color: SAND,
      negative: true,
    },
    ...(showIntraEntity
      ? [{ key: "intra", spacer: 0, value: 0, color: POLAR_DARK }]
      : []),
    {
      key: "unrealisedGains",
      spacer: extOutFrom,
      value: unrealisedGains,
      color: unrealisedGainsRaw >= 0 ? POLAR_LIGHT : SAND,
      negative: unrealisedGainsRaw < 0,
    },
    {
      key: "unattr",
      spacer: extOutFrom,
      value: unattributed,
      color: unattributedRaw >= 0 ? POLAR_LIGHT : SAND,
      negative: unattributedRaw < 0,
    },
    {
      key: "net",
      spacer: netFrom,
      value: netTo - netFrom,
      color: closing >= opening ? POLAR_LIGHT : SAND,
      negative: closing < opening,
    },
    { key: "closing", spacer: 0, value: closing, color: POLAR_DARK },
  ];

  // Tweak CHART_NUDGE_PX to shift bars right without changing width.
  const CHART_NUDGE_PX = 25;
  // Extra left padding in yearly mode (internal cashflow column hidden → chart needs to shift right).
  const CHART_YEARLY_EXTRA_PL_PCT = 2.5;
  const chartPl = showIntraEntity ? 24 : 24 + CHART_YEARLY_EXTRA_PL_PCT;

  return (
    <div
      className="w-full pr-4"
      style={{
        paddingLeft: `${chartPl}%`,
        transform: `translateX(${CHART_NUDGE_PX}px)`,
      }}
    >
      <ChartContainer config={CHART_CONFIG} className="h-32 w-full">
        <BarChart
          data={chartData}
          margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
          barCategoryGap="18%"
        >
          <YAxis hide domain={[0, "auto"]} />
          <RechartsTooltip
            content={<WaterfallTooltip />}
            cursor={{ fill: "transparent" }}
          />
          <Bar
            dataKey="spacer"
            stackId="wf"
            fill="transparent"
            radius={0}
            isAnimationActive={false}
          />
          <Bar
            dataKey="value"
            stackId="wf"
            radius={0}
            isAnimationActive={false}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.key}
                fill={entry.color}
                fillOpacity={entry.key === "net" ? 0.5 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
}

type ReportMode = "month" | "year-cal" | "year-fin";

function CashflowReportTable({
  organisationId,
  selectedMonth,
  onMonthChange,
  reportMode,
  onReportModeChange,
  fullscreen,
  onFullscreenChange,
  condensed,
  onCondensedChange,
}: {
  organisationId: string;
  selectedMonth: string | undefined;
  onMonthChange: (month: string) => void;
  reportMode: ReportMode;
  onReportModeChange: (mode: ReportMode) => void;
  fullscreen: boolean;
  onFullscreenChange: (v: boolean) => void;
  condensed: boolean;
  onCondensedChange: (v: boolean) => void;
}) {
  const now = DateTime.now();
  const currentMonthBegin = now.startOf("month").toISODate()!;
  const monthBegin = selectedMonth ?? currentMonthBegin;
  const monthDt = DateTime.fromISO(monthBegin);

  // AU financial year: FY X runs Jul 1 (X-1) to Jun 30 (X)
  const fyEndYear = monthDt.month >= 7 ? monthDt.year + 1 : monthDt.year;
  const currentFyEndYear = now.month >= 7 ? now.year + 1 : now.year;
  const startOfTomorrow = now.plus({ days: 1 }).startOf("day").toISODate()!;

  let periodBeginning: string;
  let periodEnding: string;
  let navLabel: string;
  let prevAnchor: string;
  let nextAnchor: string;
  let isAtLatest: boolean;

  if (reportMode === "month") {
    periodBeginning = monthDt.startOf("month").toISODate()!;
    periodEnding = monthDt.endOf("month").toISODate()!;
    navLabel = monthDt.toFormat("MMMM yyyy");
    prevAnchor = monthDt.minus({ months: 1 }).startOf("month").toISODate()!;
    nextAnchor = monthDt.plus({ months: 1 }).startOf("month").toISODate()!;
    isAtLatest = monthBegin === currentMonthBegin;
  } else if (reportMode === "year-cal") {
    const year = monthDt.year;
    periodBeginning = monthDt.startOf("year").toISODate()!;
    periodEnding =
      year === now.year ? startOfTomorrow : monthDt.endOf("year").toISODate()!;
    navLabel = year === now.year ? `${year} (YTD)` : `${year}`;
    prevAnchor = monthDt.minus({ years: 1 }).startOf("year").toISODate()!;
    nextAnchor = monthDt.plus({ years: 1 }).startOf("year").toISODate()!;
    isAtLatest = year === now.year;
  } else {
    // year-fin: AU FY Jul 1 (fyEndYear-1) → Jun 30 (fyEndYear)
    periodBeginning = DateTime.fromObject({
      year: fyEndYear - 1,
      month: 7,
      day: 1,
    }).toISODate()!;
    periodEnding =
      fyEndYear === currentFyEndYear
        ? startOfTomorrow
        : DateTime.fromObject({
            year: fyEndYear,
            month: 6,
            day: 30,
          }).toISODate()!;
    navLabel =
      fyEndYear === currentFyEndYear
        ? `FY${fyEndYear} (YTD)`
        : `FY${fyEndYear}`;
    prevAnchor = DateTime.fromObject({
      year: fyEndYear - 2,
      month: 7,
      day: 1,
    }).toISODate()!;
    nextAnchor = DateTime.fromObject({
      year: fyEndYear,
      month: 7,
      day: 1,
    }).toISODate()!;
    isAtLatest = fyEndYear === currentFyEndYear;
  }

  const { data, loading } = useQuery(cashflowReportDocument, {
    variables: { organisationId, periodBeginning, periodEnding },
  });
  const [expandedEntityIds, setExpandedEntityIds] = useState<Set<string>>(
    new Set(),
  );
  const [highlightedEntityIds, setHighlightedEntityIds] = useState<Set<string>>(
    new Set(),
  );
  const [extraHighlightEntityId, setExtraHighlightEntityId] = useState<
    string | null
  >(null);

  const handleHighlight = useCallback((ids: string[]) => {
    setHighlightedEntityIds(new Set(ids));
    if (ids.length === 0) {
      setExtraHighlightEntityId(null);
    }
  }, []);

  const cashflowColumns: ColumnDef<CashflowRow>[] = useMemo(
    () => [
      {
        id: "entity",
        header: "",
        cell: ({ row }) => {
          const { isGroup, isAsset, name } = row.original;
          if (isAsset) {
            const asset = row.original.assetObj;
            const infoMessages = [
              row.original.midPeriodOpeningDate &&
                `Primary has no information prior to ${DateTime.fromJSDate(
                  new Date(row.original.midPeriodOpeningDate),
                ).toFormat(
                  "d MMM yyyy",
                )} for this account, its opening balance for this period has been taken from then.`,
              row.original.midPeriodTxnHistoryBeginning &&
                `Primary does not have transaction history prior to ${DateTime.fromJSDate(
                  new Date(row.original.midPeriodTxnHistoryBeginning),
                ).toFormat(
                  "d MMM yyyy",
                )} for this account, so balance differences before then are recorded as cash inflows and cash outflows.`,
              isStripeAsset(asset) &&
                "We do not receive transactional data from Stripe, so all balance differences are recorded as cash inflows and cash outflows.",
            ].filter((message): message is string => Boolean(message));
            return (
              <div className="flex items-center gap-2 pl-4">
                <Icon
                  size="6"
                  imageUrl={asset?.logoUrl ?? undefined}
                  fallbackIcon={<PiBuildingOffice />}
                  className="shrink-0"
                  bordered={false}
                />
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <Link
                      to="/assets/$assetId"
                      params={{ assetId: asset?.id ?? "" }}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline"
                    >
                      {name}
                    </Link>
                    {infoMessages.length > 0 && (
                      <TooltipProvider>
                        <Tooltip delayDuration={50}>
                          <TooltipTrigger asChild>
                            <span
                              className="cursor-default"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PiInfo className="text-tertiary" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64 whitespace-pre-line text-left">
                            {infoMessages.join("\n\n")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <span className="text-tertiary">
                    {row.original.assetNativeCurrency}
                  </span>
                </div>
              </div>
            );
          }
          if (isGroup) {
            return <span className="text-left font-semibold">{name}</span>;
          }
          return <span className="text-left">{name}</span>;
        },
      },
      {
        id: "opening",
        header: () => <RightHeader title="Opening" />,
        cell: ({ row }) => (
          <NumCell amount={row.original.opening} bold={row.original.isGroup} />
        ),
      },
      {
        id: "externalIn",
        header: () => <RightHeader title="Cash inflows" />,
        cell: ({ row }) =>
          reportMode === "month" ? (
            <NumCellWithHover
              amount={row.original.externalIn}
              bold={row.original.isGroup}
              notable={row.original.externalInNotable}
              otherCount={row.original.externalInOtherCount}
              entityName={row.original.name}
              assumedExternalCashflowNet={
                row.original.assumedExternalCashflowNet
              }
              assumedExternalCashflowNetCurrency={row.original.currency}
            />
          ) : (
            <NumCell
              amount={row.original.externalIn}
              bold={row.original.isGroup}
            />
          ),
      },
      {
        id: "externalOut",
        header: () => <RightHeader title="Cash outflows" />,
        cell: ({ row }) =>
          reportMode === "month" ? (
            <NumCellWithHover
              amount={row.original.externalOut}
              isOut
              bold={row.original.isGroup}
              notable={row.original.externalOutNotable}
              otherCount={row.original.externalOutOtherCount}
              entityName={row.original.name}
              assumedExternalCashflowNet={
                row.original.assumedExternalCashflowNet
              }
              assumedExternalCashflowNetCurrency={row.original.currency}
            />
          ) : (
            <NumCell
              amount={row.original.externalOut}
              isOut
              bold={row.original.isGroup}
            />
          ),
      },
      {
        id: "interOrgIn",
        header: () => <RightHeader title="Intracompany inflows" />,
        cell: ({ row }) => {
          if (row.original.isGroup) {
            return (
              <div className="text-right font-semibold text-secondary">—</div>
            );
          }
          if (reportMode !== "month") {
            return (
              <InterOrgBreakdownCell
                amount={row.original.interOrgIn}
                entityBreakdown={row.original.interOrgInByEntity}
                assetBreakdown={row.original.interOrgInByAsset}
                onExtraHighlight={setExtraHighlightEntityId}
              />
            );
          }
          return (
            <NumCellWithHover
              amount={row.original.interOrgIn}
              notable={row.original.interOrgInNotable}
              otherCount={row.original.interOrgInOtherCount}
              entityName={row.original.name}
              onHighlight={handleHighlight}
              onExtraHighlight={setExtraHighlightEntityId}
            />
          );
        },
      },
      {
        id: "interOrgOut",
        header: () => <RightHeader title="Intracompany outflows" />,
        cell: ({ row }) => {
          if (row.original.isGroup) {
            return (
              <div className="text-right font-semibold text-secondary">—</div>
            );
          }
          if (reportMode !== "month") {
            return (
              <InterOrgBreakdownCell
                amount={row.original.interOrgOut}
                isOut
                entityBreakdown={row.original.interOrgOutByEntity}
                assetBreakdown={row.original.interOrgOutByAsset}
                onExtraHighlight={setExtraHighlightEntityId}
              />
            );
          }
          return (
            <NumCellWithHover
              amount={row.original.interOrgOut}
              isOut
              notable={row.original.interOrgOutNotable}
              otherCount={row.original.interOrgOutOtherCount}
              entityName={row.original.name}
              onHighlight={handleHighlight}
              onExtraHighlight={setExtraHighlightEntityId}
            />
          );
        },
      },
      ...(reportMode === "month"
        ? [
            {
              id: "intraEntity",
              header: () => <RightHeader title="Internal cashflow" />,
              cell: ({ row }: { row: Row<CashflowRow> }) => {
                const {
                  isAsset,
                  intraEntityIn,
                  intraEntityOut,
                  isGroup,
                  intraEntityNotable,
                  intraEntityOtherCount,
                  name,
                } = row.original;
                // Asset rows: directional net (negative = outflow, positive = inflow)
                // Entity/group rows: always net to zero (internal flows cancel within entity)
                const amount = isAsset
                  ? intraEntityIn.minus(intraEntityOut)
                  : new Decimal(0);
                const hasIntraEntity = !intraEntityOut.isZero();
                return (
                  <div className="relative">
                    <NumCellWithHover
                      amount={amount}
                      bold={isGroup}
                      notable={intraEntityNotable}
                      otherCount={intraEntityOtherCount}
                      entityName={name}
                    />
                    {!isAsset && hasIntraEntity && (
                      <span className="absolute top-0 -right-2.5 text-tertiary text-xs">
                        *
                      </span>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      {
        id: "unrealisedGains",
        header: () => (
          <div className="flex items-center justify-end gap-1">
            <TooltipProvider>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    <PiInfo className="text-tertiary" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 text-left">
                  The change in value of unit-priced assets (e.g. managed funds)
                  caused by unit price movement rather than transactions. E.g.
                  if a fund's unit price rises from $1.00 to $1.05 over the
                  month, this shows the resulting gain on units held throughout
                  the period.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <RightHeader title="Unrealised gains" />
          </div>
        ),
        cell: ({ row }) => {
          const { unrealisedGains } = row.original;
          const display = unrealisedGains.toDecimalPlaces(2).isZero()
            ? new Decimal(0)
            : unrealisedGains;
          return (
            <div className="text-right tabular-nums">{fmtAmount(display)}</div>
          );
        },
      },
      {
        id: "unknown",
        header: () => (
          <div className="flex items-center justify-end gap-1">
            <TooltipProvider>
              <Tooltip delayDuration={50}>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    <PiInfo className="text-tertiary" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 text-left">
                  Small unattributed cashflow amounts may be the result of FX
                  rate movements during the month. Larger amounts may indicate
                  incomplete historical transaction data.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <RightHeader title="Unattributed cashflow" />
          </div>
        ),
        cell: ({ row }) => {
          const { unattributed } = row.original;
          const display = unattributed.toDecimalPlaces(2).isZero()
            ? new Decimal(0)
            : unattributed;
          return (
            <div className="text-right tabular-nums">{fmtAmount(display)}</div>
          );
        },
      },
      {
        id: "netCashflow",
        header: () => <RightHeader title="Net cashflow" />,
        cell: ({ row }) => (
          <NumCell amount={row.original.net} bold={row.original.isGroup} />
        ),
      },
      {
        id: "closing",
        header: () => <RightHeader title="Closing" />,
        cell: ({ row }) => (
          <NumCell amount={row.original.closing} bold={row.original.isGroup} />
        ),
      },
    ],
    [handleHighlight, reportMode],
  );

  const report = data?.organisation?.cashflowReport;
  const orgName = data?.organisation?.name ?? "Group";

  const rows: CashflowRow[] =
    report?.businessEntityBreakdown.map((be) => {
      const interOrgIn = new Decimal(be.values.interOrgIn.amount);
      const interOrgOut = new Decimal(be.values.interOrgOut.amount);
      const externalIn = new Decimal(be.values.externalIn.amount);
      const externalOut = new Decimal(be.values.externalOut.amount);
      return {
        id: be.businessEntity.id,
        name: be.businessEntity.name,
        opening: new Decimal(be.values.opening.amount),
        externalIn,
        externalOut,
        interOrgIn,
        interOrgOut,
        intraEntityIn: new Decimal(be.values.intraEntityIn.amount),
        intraEntityOut: new Decimal(be.values.intraEntityOut.amount),
        closing: new Decimal(be.values.closing.amount),
        net: new Decimal(be.values.net.amount),
        unattributed: new Decimal(be.values.unattributed.amount),
        assumedExternalCashflowNet: new Decimal(
          be.values.assumedExternalCashflowNet.amount,
        ),
        unrealisedGains: new Decimal(be.values.unrealisedGains.amount),
        currency: be.values.opening.currency,
        externalInNotable: be.values.externalInNotable,
        externalInOtherCount: be.values.externalInOtherCount,
        externalOutNotable: be.values.externalOutNotable,
        externalOutOtherCount: be.values.externalOutOtherCount,
        interOrgInNotable: be.values.interOrgInNotable,
        interOrgInOtherCount: be.values.interOrgInOtherCount,
        interOrgOutNotable: be.values.interOrgOutNotable,
        interOrgOutOtherCount: be.values.interOrgOutOtherCount,
        intraEntityNotable: be.values.intraEntityNotable,
        intraEntityOtherCount: be.values.intraEntityOtherCount,
        interOrgInByEntity: be.values.interOrgInByEntity.map((e) => ({
          entityId: e.businessEntity.id,
          entityName: e.businessEntity.name,
          amount: new Decimal(e.amount.amount),
        })),
        interOrgOutByEntity: be.values.interOrgOutByEntity.map((e) => ({
          entityId: e.businessEntity.id,
          entityName: e.businessEntity.name,
          amount: new Decimal(e.amount.amount),
        })),
        interOrgInByAsset: [],
        interOrgOutByAsset: [],
      };
    }) ?? [];

  const assetRowsById = new Map<string, CashflowRow[]>(
    report?.businessEntityBreakdown.map((be) => [
      be.businessEntity.id,
      [...be.assetBreakdown]
        .sort((a, b) =>
          new Decimal(b.values.closing.amount).comparedTo(
            new Decimal(a.values.closing.amount),
          ),
        )
        .map((a) => {
          const assetInterOrgIn = new Decimal(a.values.interOrgIn.amount);
          const assetInterOrgOut = new Decimal(a.values.interOrgOut.amount);
          const assetExternalIn = new Decimal(a.values.externalIn.amount);
          const assetExternalOut = new Decimal(a.values.externalOut.amount);
          return {
            id: `${be.businessEntity.id}:${a.asset.id}`,
            name: a.asset.name,
            parentId: be.businessEntity.id,
            isAsset: true,
            assetObj: a.asset,
            assetNativeCurrency: a.values.opening.currency,
            midPeriodOpeningDate: a.midPeriodOpeningDate ?? undefined,
            midPeriodTxnHistoryBeginning:
              a.midPeriodTxnHistoryBeginning ?? undefined,
            opening: new Decimal(a.values.opening.amount),
            externalIn: assetExternalIn,
            externalOut: assetExternalOut,
            interOrgIn: assetInterOrgIn,
            interOrgOut: assetInterOrgOut,
            intraEntityIn: new Decimal(a.values.intraEntityIn.amount),
            intraEntityOut: new Decimal(a.values.intraEntityOut.amount),
            closing: new Decimal(a.values.closing.amount),
            net: new Decimal(a.values.net.amount),
            unattributed: new Decimal(a.values.unattributed.amount),
            assumedExternalCashflowNet: new Decimal(
              a.values.assumedExternalCashflowNet.amount,
            ),
            unrealisedGains: new Decimal(a.values.unrealisedGains.amount),
            currency: a.values.opening.currency,
            externalInNotable: a.values.externalInNotable,
            externalInOtherCount: a.values.externalInOtherCount,
            externalOutNotable: a.values.externalOutNotable,
            externalOutOtherCount: a.values.externalOutOtherCount,
            interOrgInNotable: a.values.interOrgInNotable,
            interOrgInOtherCount: a.values.interOrgInOtherCount,
            interOrgOutNotable: a.values.interOrgOutNotable,
            interOrgOutOtherCount: a.values.interOrgOutOtherCount,
            intraEntityNotable: a.values.intraEntityNotable,
            intraEntityOtherCount: a.values.intraEntityOtherCount,
            interOrgInByEntity: [],
            interOrgOutByEntity: [],
            interOrgInByAsset: a.values.interOrgInByAsset.map((e) => ({
              assetId: e.asset.id,
              assetName: e.asset.name,
              entityName:
                (e.asset as { businessEntity?: { name?: string } })
                  .businessEntity?.name ?? "",
              amount: new Decimal(e.amount.amount),
            })),
            interOrgOutByAsset: a.values.interOrgOutByAsset.map((e) => ({
              assetId: e.asset.id,
              assetName: e.asset.name,
              entityName:
                (e.asset as { businessEntity?: { name?: string } })
                  .businessEntity?.name ?? "",
              amount: new Decimal(e.amount.amount),
            })),
          };
        }),
    ]),
  );

  const unattributedByProvider = [...assetRowsById.values()]
    .flat()
    .reduce((totals, row) => {
      const providerName = dataProviderName(row.assetObj);
      totals.set(
        providerName,
        (totals.get(providerName) ?? new Decimal(0)).plus(row.unattributed),
      );
      return totals;
    }, new Map<string, Decimal>());
  const unattributedByProviderRows = [...unattributedByProvider.entries()]
    .map(([providerName, amount]) => ({ providerName, amount }))
    .sort((a, b) => b.amount.abs().comparedTo(a.amount.abs()));
  const unattributedByProviderTotal = unattributedByProviderRows.reduce(
    (s, r) => s.plus(r.amount),
    new Decimal(0),
  );

  const sum = useCallback(
    (fn: (r: CashflowRow) => Decimal) =>
      rows.reduce((s, r) => s.plus(fn(r)), new Decimal(0)),
    [rows],
  );

  const groupExternalIn = sum((r) => r.externalIn);
  const groupExternalOut = sum((r) => r.externalOut);

  const groupRow: CashflowRow = useMemo(
    () => ({
      id: "group",
      name: orgName,
      opening: sum((r) => r.opening),
      externalIn: groupExternalIn,
      externalOut: groupExternalOut,
      interOrgIn: new Decimal(0),
      interOrgOut: new Decimal(0),
      intraEntityIn: sum((r) => r.intraEntityIn),
      intraEntityOut: sum((r) => r.intraEntityOut),
      closing: sum((r) => r.closing),
      net: sum((r) => r.net),
      unattributed: sum((r) => r.unattributed),
      assumedExternalCashflowNet: sum((r) => r.assumedExternalCashflowNet),
      unrealisedGains: sum((r) => r.unrealisedGains),
      currency: rows[0]?.currency ?? "",
      isGroup: true,
      externalInNotable: rows.flatMap((r) => r.externalInNotable),
      externalInOtherCount: rows.reduce(
        (s, r) => s + r.externalInOtherCount,
        0,
      ),
      externalOutNotable: rows.flatMap((r) => r.externalOutNotable),
      externalOutOtherCount: rows.reduce(
        (s, r) => s + r.externalOutOtherCount,
        0,
      ),
      interOrgInNotable: [],
      interOrgInOtherCount: 0,
      interOrgOutNotable: [],
      interOrgOutOtherCount: 0,
      intraEntityNotable: rows.flatMap((r) => r.intraEntityNotable),
      intraEntityOtherCount: rows.reduce(
        (s, r) => s + r.intraEntityOtherCount,
        0,
      ),
      interOrgInByEntity: [],
      interOrgOutByEntity: [],
      interOrgInByAsset: [],
      interOrgOutByAsset: [],
    }),
    [rows, sum, groupExternalIn, groupExternalOut, orgName],
  );

  const isAllZero = (r: CashflowRow) =>
    r.opening.isZero() &&
    r.closing.isZero() &&
    r.externalIn.isZero() &&
    r.externalOut.isZero() &&
    r.interOrgIn.isZero() &&
    r.interOrgOut.isZero() &&
    r.intraEntityIn.isZero() &&
    r.intraEntityOut.isZero();

  const sortedEntityRows = rows.sort((a, b) => b.closing.comparedTo(a.closing));
  const tableRows: CashflowRow[] = [
    ...sortedEntityRows
      .filter((r) => !condensed || !isAllZero(r))
      .flatMap((r) => {
        const sub = expandedEntityIds.has(r.id)
          ? (assetRowsById.get(r.id) ?? []).filter(
              (a) => !condensed || !isAllZero(a),
            )
          : [];
        return [r, ...sub];
      }),
    groupRow,
  ];

  const copyToClipboard = useCallback(() => {
    const fmt = (d: Decimal) => d.toDecimalPlaces(2).toFixed(2);
    const internalCashflow = (r: CashflowRow) =>
      r.isAsset ? r.intraEntityIn.minus(r.intraEntityOut) : r.intraEntityOut;

    const headers = [
      "Entity",
      "Currency",
      "Opening",
      "Cash inflows",
      "Cash outflows",
      "Intracompany inflows",
      "Intracompany outflows",
      "Internal cashflow",
      "Unrealised gains",
      "Unattributed cashflow",
      "Net cashflow",
      "Closing",
    ];

    // Always fully expanded for the copy
    const allRows: CashflowRow[] = [
      ...sortedEntityRows.flatMap((r) => [
        r,
        ...(assetRowsById.get(r.id) ?? []),
      ]),
      groupRow,
    ];

    const lines = [
      headers.join("\t"),
      ...allRows.map((r) =>
        [
          r.isAsset ? `  ${r.name}` : r.name,
          r.assetNativeCurrency ?? "",
          fmt(r.opening),
          fmt(r.externalIn),
          fmt(r.externalOut),
          fmt(r.interOrgIn),
          fmt(r.interOrgOut),
          fmt(internalCashflow(r)),
          fmt(r.unrealisedGains),
          fmt(r.unattributed),
          fmt(r.net),
          fmt(r.closing),
        ].join("\t"),
      ),
    ].join("\n");

    void navigator.clipboard.writeText(lines);
  }, [sortedEntityRows, assetRowsById, groupRow]);

  // FLIP animation refs
  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const flipFirstRect = useRef<DOMRect | null>(null);

  // Record position BEFORE state change, then fire onFullscreenChange
  const handleFullscreenToggle = useCallback(
    (v: boolean) => {
      if (cardWrapperRef.current) {
        flipFirstRect.current = cardWrapperRef.current.getBoundingClientRect();
      }
      onFullscreenChange(v);
    },
    [onFullscreenChange],
  );

  // FLIP: runs synchronously after the DOM updates with the new fullscreen state
  useLayoutEffect(() => {
    const el = cardWrapperRef.current;
    const first = flipFirstRect.current;
    if (!el || !first) {
      return;
    }
    flipFirstRect.current = null;

    const last = el.getBoundingClientRect();
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    const sx = first.width / last.width;
    const sy = first.height / last.height;

    // Skip if nothing changed
    if (
      Math.abs(dx) < 1 &&
      Math.abs(dy) < 1 &&
      Math.abs(sx - 1) < 0.01 &&
      Math.abs(sy - 1) < 0.01
    ) {
      return;
    }

    // INVERT: snap to where it was
    el.style.transformOrigin = "top left";
    el.style.transform = `translate(${dx}px, ${dy}px) scaleX(${sx}) scaleY(${sy})`;
    el.style.transition = "none";

    // PLAY: animate to natural position on the next two frames
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition = "transform 350ms cubic-bezier(0.16, 1, 0.3, 1)";
        el.style.transform = "translate(0,0) scaleX(1) scaleY(1)";
        const cleanup = () => {
          el.style.transition = "";
          el.style.transform = "";
          el.style.transformOrigin = "";
        };
        el.addEventListener("transitionend", cleanup, { once: true });
      });
    });
  }, []);

  return (
    <div
      ref={cardWrapperRef}
      className={
        fullscreen
          ? "fixed inset-0 z-50 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]"
          : ""
      }
    >
      <Card
        className={
          fullscreen
            ? "flex min-h-full flex-col gap-4 rounded-none"
            : "flex flex-col gap-4"
        }
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleFullscreenToggle(!fullscreen)}
            >
              {fullscreen ? <PiX /> : <PiArrowsOut />}
            </Button>
            <Txt variant="section-title">Cashflow Report</Txt>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-full text-tertiary hover:bg-neutral-muted"
                >
                  <PiSlidersHorizontal className="text-base" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Report period mode</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => {
                    onMonthChange(currentMonthBegin);
                    onReportModeChange("month");
                  }}
                >
                  <PiCalendar
                    className={cn(
                      "mr-2 shrink-0",
                      reportMode === "month" && "text-info",
                    )}
                  />
                  <span
                    className={cn(
                      reportMode === "month" && "font-medium text-info",
                    )}
                  >
                    Monthly
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onReportModeChange("year-cal")}
                >
                  <PiCalendar
                    className={cn(
                      "mr-2 shrink-0",
                      reportMode === "year-cal" && "text-info",
                    )}
                  />
                  <span
                    className={cn(
                      reportMode === "year-cal" && "font-medium text-info",
                    )}
                  >
                    Yearly (calendar)
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onReportModeChange("year-fin")}
                >
                  <PiMoney
                    className={cn(
                      "mr-2 shrink-0",
                      reportMode === "year-fin" && "text-info",
                    )}
                  />
                  <span
                    className={cn(
                      reportMode === "year-fin" && "font-medium text-info",
                    )}
                  >
                    Yearly (financial)
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Report settings</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onCondensedChange(!condensed)}>
                  {condensed ? (
                    <PiEye className="mr-2 shrink-0" />
                  ) : (
                    <PiEyeClosed className="mr-2 shrink-0" />
                  )}
                  {condensed ? "Show empty rows" : "Hide empty rows"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={copyToClipboard}>
                  <PiCopy className="mr-2 shrink-0" />
                  Copy to clipboard
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMonthChange(prevAnchor)}
            >
              <PiCaretLeft />
            </Button>
            <span className="min-w-[7rem] text-center font-medium text-sm">
              {navLabel}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={isAtLatest}
              onClick={() => onMonthChange(nextAnchor)}
            >
              <PiCaretRight />
            </Button>
          </div>
        </div>
        {report && sortedEntityRows.length > 0 && (
          <CashflowWaterfallChart
            entityRows={sortedEntityRows}
            groupRow={groupRow}
            showIntraEntity={reportMode === "month"}
          />
        )}
        {loading && !report ? (
          <div className="flex flex-col items-center gap-3 py-12 text-secondary text-sm">
            <PrimaryFullScreenSpinner />
            Calculating monthly cashflow across all entities
          </div>
        ) : (
          <DataTable
            className="-mt-8"
            data={tableRows}
            columns={cashflowColumns}
            cellClassName="py-5"
            headerCellClassName="align-bottom pb-1"
            fixedLayoutPcWidths={{
              entity: 22,
              opening: 8,
              externalIn: 8,
              externalOut: 8,
              interOrgIn: 7,
              interOrgOut: 7,
              intraEntity: 7,
              unrealisedGains: 7,
              unknown: 7,
              netCashflow: 7,
              closing: 8,
            }}
            isLoadingData={loading}
            manualPagination
            hidePagination
            showCsvExport={false}
            pageSize={Math.max(tableRows.length, 1)}
            emptyState={
              <Txt variant="body">No cashflow data for this month.</Txt>
            }
            onRowClick={(row) => {
              const { id, isAsset, isGroup } = row.original;
              if (!isAsset && !isGroup && id !== "group") {
                setExpandedEntityIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) {
                    next.delete(id);
                  } else {
                    next.add(id);
                  }
                  return next;
                });
              }
            }}
            getRowClassName={(row: Row<CashflowRow>) => {
              const { id, isAsset, parentId, isGroup } = row.original;
              if (id === "group") {
                return "cursor-default font-semibold";
              }
              if (isAsset) {
                const pid = parentId ?? "";
                if (pid === extraHighlightEntityId) {
                  return "cursor-default bg-warning-muted/60 text-sm [&>td]:!py-2";
                }
                if (highlightedEntityIds.has(pid)) {
                  return "cursor-default bg-info-muted/60 text-sm [&>td]:!py-2";
                }
                return "cursor-default bg-neutral-muted text-sm [&>td]:!py-2";
              }
              if (!isGroup) {
                const base = "cursor-pointer";
                if (id === extraHighlightEntityId) {
                  return cn(base, "bg-warning-muted");
                }
                if (highlightedEntityIds.has(id)) {
                  return cn(base, "bg-info-muted");
                }
                return base;
              }
              if (id === extraHighlightEntityId) {
                return "bg-warning-muted";
              }
              if (highlightedEntityIds.has(id)) {
                return "bg-info-muted";
              }
              return undefined;
            }}
          />
        )}
        {report && unattributedByProviderRows.length > 0 && (
          <div
            className={cn(
              "mt-4 w-1/3 rounded-md border border-dashed p-4",
              unattributedSeverityClasses(unattributedByProviderTotal),
            )}
          >
            <Txt variant="section-title">
              Unattributed data provider summary
            </Txt>
            <div className="mt-3 flex flex-col gap-1">
              {unattributedByProviderRows.map(({ providerName, amount }) => (
                <div
                  key={providerName}
                  className="flex items-center justify-between border-b py-1 text-sm"
                >
                  <span>{providerName}</span>
                  <span className="font-medium tabular-nums">
                    {fmtAmount(amount)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1 text-sm">
                <span className="font-semibold">Total</span>
                <span className="font-semibold tabular-nums">
                  {fmtAmount(unattributedByProviderTotal)}
                </span>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function View() {
  const {
    organisationId,
    cashflowMonth,
    reportMode,
    fullscreen,
    hideZeroRows,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: orgsData } = useSuspenseQuery(orgsDocument);

  const organisations = [...(orgsData?.organisations ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const setOrganisationId = (id: string) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        organisationId: id,
        cashflowMonth: undefined,
      }),
    });
  };

  const setCashflowMonth = (month: string) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        cashflowMonth: month,
      }),
    });
  };

  const setFullscreen = (v: boolean) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        fullscreen: v || undefined,
      }),
    });
  };

  const setHideZeroRows = (v: boolean) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        hideZeroRows: v || undefined,
      }),
    });
  };

  const setReportMode = (mode: ReportMode) => {
    void navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        reportMode: mode === "month" ? undefined : mode,
      }),
    });
  };

  return (
    <AdminPageContainer fullWidth testId="admin-cashflow-reports">
      <AdminPageHeader
        title="Cashflow Reports"
        subtitle="Cashflow report by entity, and external transaction match proposals"
        rightContent={
          <Select value={organisationId} onValueChange={setOrganisationId}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Select an organisation..." />
            </SelectTrigger>
            <SelectContent>
              {organisations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  <div className="flex flex-row gap-2">
                    <Icon
                      size="5"
                      imageUrl={org?.squareLogoUrl ?? undefined}
                      fallbackIcon={<PiBuildingOffice />}
                    />
                    {org.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="flex flex-col gap-6">
        {organisationId && (
          <CashflowReportTable
            organisationId={organisationId}
            selectedMonth={cashflowMonth}
            onMonthChange={setCashflowMonth}
            reportMode={reportMode}
            onReportModeChange={setReportMode}
            fullscreen={fullscreen ?? false}
            onFullscreenChange={setFullscreen}
            condensed={hideZeroRows ?? false}
            onCondensedChange={setHideZeroRows}
          />
        )}
        {organisationId && <ProposalsList organisationId={organisationId} />}
      </div>
    </AdminPageContainer>
  );
}

function ProposalsList({ organisationId }: { organisationId: string }) {
  const { data, loading, networkStatus, fetchMore } = useQuery(
    proposalsDocument,
    {
      variables: {
        first: PAGE_SIZE,
        after: null,
        organisationIds: [organisationId],
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const proposals =
    data?.externalTransactionMatchProposals.externalTransactionMatchProposals ??
    [];
  const pageInfo = data?.externalTransactionMatchProposals.pageInfo;
  const totalCount = data?.externalTransactionMatchProposals.totalCount;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <PiCaretDown className="shrink-0 text-tertiary" />
          ) : (
            <PiCaretRight className="shrink-0 text-tertiary" />
          )}
          <Txt variant="section-title">
            Transaction match proposals
            {totalCount != null ? ` (${totalCount})` : ""}
          </Txt>
        </button>
        {expanded && (
          <>
            <DataTable
              data={showAll ? proposals : proposals.slice(0, 5)}
              columns={columns}
              isLoadingData={
                loading && networkStatus !== NetworkStatus.fetchMore
              }
              manualPagination
              hidePagination
              showCsvExport={false}
              pageSize={showAll ? PAGE_SIZE : 5}
              emptyState={
                <Txt variant="body">
                  No match proposals found for this organisation.
                </Txt>
              }
            />
            {(!showAll || pageInfo?.hasNextPage) && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!showAll) {
                      setShowAll(true);
                    } else {
                      void fetchMore({
                        variables: { after: pageInfo?.endCursor },
                      });
                    }
                  }}
                  loading={networkStatus === NetworkStatus.fetchMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
